// Client-side mirror of the backend attribute-name constraints, used to validate names without extra
// backend round-trips. Keep in sync with:
//   - AttributeDefinition.ATTRIBUTE_NAME_MAX_LENGTH / IsValidAttributeName (Entities/Attributes)
//   - Code.RESERVED_FIELDS (Entities/Code.cs)
//   - SemanticWebConfiguration.COMMON_IRI_ATTRIBUTE_NAME (Entities/SemanticWebConfiguration.cs)
// The "library in use / existing codes" checks are intentionally NOT mirrored here.

export const ATTRIBUTE_NAME_MAX_LENGTH = 64;

export const RESERVED_ATTRIBUTE_NAMES = [
    // Code.RESERVED_FIELDS
    'Name',
    'Description',
    'IsValid',
    // Added by AttributeDefinitionCreateOrUpdateModel.Validate
    'Identity',
    'DateCreated',
    'DateUpdated',
    'AttachmentKey',
    'ID',
    'IsReleaseReady',
    'CommonLibraryIRI'
];

const VALID_ATTRIBUTE_NAME_REGEX = new RegExp(`^[a-zA-Z_][a-zA-Z0-9_]{0,${ATTRIBUTE_NAME_MAX_LENGTH - 1}}$`);

/**
 * Validates an attribute name against the backend rules (except existing-codes checks).
 * @param name The candidate name.
 * @param existingNames Other attribute names in the library to check uniqueness against.
 * @returns An error message, or null when the name is valid.
 */
export function validateAttributeName(name: string, existingNames: string[] = []): string | null {
    const trimmed = (name ?? '').trim();

    if (!trimmed) {
        return 'Name cannot be empty';
    }
    if (!VALID_ATTRIBUTE_NAME_REGEX.test(trimmed)) {
        return `Name must start with a letter or underscore, contain only [a-zA-Z0-9_], and be at most ${ATTRIBUTE_NAME_MAX_LENGTH} characters`;
    }
    if (RESERVED_ATTRIBUTE_NAMES.some(reserved => reserved.toLowerCase() === trimmed.toLowerCase())) {
        return `'${trimmed}' is a reserved name and cannot be used`;
    }
    if (existingNames.some(existing => existing.toLowerCase() === trimmed.toLowerCase())) {
        return 'An attribute with this name already exists in the library';
    }

    return null;
}
